import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { hgLink, trackOutbound, trackConversion, HG_TRIAL_PATH } from "../data/site";
import { Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Reveal = ({ children, delay = 0, className = "", y = 28 }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

export const Eyebrow = ({ children, accent = "text-neutral-400" }) => (
  <p className={`font-mono text-xs font-bold tracking-[0.22em] uppercase ${accent}`}>{children}</p>
);

export const TrialButton = ({ campaign, page = "unknown", children = "Book a Free Trial Class", className = "", testId = "trial-cta-btn", to, location }) => (
  <Link
    to={to || hgLink(HG_TRIAL_PATH, campaign, location)}
    onClick={() => trackOutbound(page, campaign)}
    data-testid={testId}
    className={`inline-flex items-center justify-center gap-2 bg-[#111315] border border-white/20 hover:bg-neutral-200 hover:text-[#0A0B0D] text-white font-display font-bold uppercase tracking-wide text-base px-8 py-4 transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${className}`}
  >
    {children}
    <span aria-hidden="true">→</span>
  </Link>
);

export const GhostButton = ({ to, children, className = "", testId, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center justify-center gap-2 border border-current font-display font-bold uppercase tracking-wide text-base px-8 py-4 transition-colors duration-200 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${className}`}
  >
    {children}
  </Link>
);

export const Marquee = ({ items, className = "" }) => (
  <div className={`overflow-hidden border-y border-white/10 bg-[#0A0B0D] py-5 ${className}`} aria-hidden="true">
    <div className="marquee-track flex gap-12 whitespace-nowrap">
      {[...items, ...items, ...items].map((item, i) => (
        <span key={i} className="font-display font-black uppercase tracking-wide text-xl text-white/40 flex items-center gap-12">
          {item} <span className="text-neutral-400">·</span>
        </span>
      ))}
    </div>
  </div>
);

const Counter = ({ value, suffix, decimals = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / 1400, 1);
      setN(value * (1 - Math.pow(1 - p, 3)));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);
  return (
    <span ref={ref} className="tabular-nums">
      {n.toFixed(decimals)}{suffix}
    </span>
  );
};

export const StatBar = ({ stats, dark = true }) => (
  <div className={`grid grid-cols-2 md:grid-cols-4 gap-px ${dark ? "bg-white/10" : "bg-black/10"}`} data-testid="stat-counters">
    {stats.map((s) => (
      <div key={s.label} className={`${dark ? "bg-[#0A0B0D] text-white" : "bg-white text-[#1E2229]"} p-6 md:p-8`}>
        <div className="font-display font-black text-4xl md:text-5xl text-neutral-400">
          <Counter value={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
        </div>
        <div className={`mt-1 text-sm ${dark ? "text-white/60" : "text-[#5A6270]"}`}>{s.label}</div>
      </div>
    ))}
  </div>
);

export const FAQBlock = ({ faqs, testPrefix = "faq" }) => (
  <Accordion type="single" collapsible className="w-full">
    {faqs.map((f, i) => (
      <AccordionItem key={i} value={`item-${i}`} data-testid={`${testPrefix}-accordion-item-${i}`} className="border-b border-black/10">
        <AccordionTrigger className="text-left font-display font-bold text-lg uppercase tracking-tight hover:text-neutral-500 hover:no-underline py-5">
          {f.q}
        </AccordionTrigger>
        <AccordionContent className="text-[#374151] text-base leading-relaxed pb-6 max-w-3xl">{f.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const VideoPlayer = ({ src, poster, title, portrait = false, testId = "video-player", caption, dark = false, className = "" }) => {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  return (
    <figure className={className}>
      <div className={`relative overflow-hidden border ${dark ? "border-white/15" : "border-black/10"} bg-[#0A0B0D] ${portrait ? "aspect-[9/16] max-w-sm" : "aspect-video"}`}>
        <video ref={ref} controls={started} playsInline preload="none" poster={poster} className="w-full h-full object-cover" aria-label={title} data-testid={testId}>
          <source src={src} type="video/mp4" />
        </video>
        {!started && (
          <button type="button" onClick={() => { setStarted(true); ref.current?.play(); }} aria-label={`Play video: ${title}`} data-testid={`${testId}-play-btn`}
            className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/15 transition-colors group cursor-pointer">
            <span className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-white text-[#0A0B0D] rounded-full group-hover:scale-110 transition-transform">
              <Play size={26} fill="currentColor" className="ml-1" />
            </span>
          </button>
        )}
      </div>
      {caption && <figcaption className={`mt-2 font-mono text-[11px] uppercase tracking-widest ${dark ? "text-white/50" : "text-[#5A6270]"}`}>{caption}</figcaption>}
    </figure>
  );
};

export const NewsletterForm = ({ dark = false, sourcePage = "footer" }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email, source_page: sourcePage });
      trackConversion("sign_up", { method: "newsletter", source_page: sourcePage });
      toast.success("You're on the list. Train hard.");
      setEmail("");
    } catch {
      toast.error("Something went wrong — try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex gap-2 max-w-md" data-testid="newsletter-form">
      <label htmlFor={`nl-${sourcePage}`} className="sr-only">Email address</label>
      <input
        id={`nl-${sourcePage}`}
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email"
        data-testid="newsletter-email-input"
        className={`flex-1 px-4 py-3 text-base border ${dark ? "bg-white/5 border-white/20 text-white placeholder:text-white/40" : "bg-white border-black/20 text-[#1E2229]"} focus:outline-none focus:border-[#0A0B0D]`}
      />
      <button
        type="submit"
        disabled={busy}
        data-testid="newsletter-submit-btn"
        className="bg-[#111315] border border-white/20 hover:bg-neutral-200 hover:text-[#0A0B0D] text-white font-display font-bold uppercase tracking-wide px-6 py-3 transition-colors disabled:opacity-60"
      >
        {busy ? "..." : "Join"}
      </button>
    </form>
  );
};

export const SectionShell = ({ children, dark = false, className = "", id }) => (
  <section id={id} className={`${dark ? "bg-[#0A0B0D] text-white" : "bg-[#F7F8F9] text-[#1E2229]"} py-20 lg:py-28 ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);
